import { format } from 'date-fns';
import type { TimelineEvent } from '../types';
import { pinEvent } from '../api/client';

interface Props {
  event: TimelineEvent;
  isSelected: boolean;
  onSelect: (event: TimelineEvent) => void;
  onPinToggle: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  received: 'Received', sent: 'Sent', replied: 'Replied', forwarded: 'Forwarded',
  moved: 'Moved', deleted: 'Deleted', 'attachment-added': 'Attachment',
  flagged: 'Flagged', read: 'Read', 'label-changed': 'Label Changed',
};

export default function TimelineEventItem({ event, isSelected, onSelect, onPinToggle }: Props) {
  async function togglePin(e: React.MouseEvent) {
    e.stopPropagation();
    await pinEvent(event.id);
    onPinToggle();
  }

  return (
    <div
      onClick={() => onSelect(event)}
      style={{
        ...styles.item,
        borderLeft: `4px solid ${event.color || '#3B82F6'}`,
        background: isSelected ? '#eff6ff' : '#fff',
        opacity: event.type === 'deleted' ? 0.7 : 1,
      }}
    >
      {/* Left: dot + type badge */}
      <div style={styles.left}>
        <span style={{ ...styles.badge, background: event.color || '#3B82F6' }}>
          {TYPE_LABELS[event.type] || event.type}
        </span>
        <span style={styles.time}>
          {format(new Date(event.timestamp), 'MMM d, HH:mm')}
        </span>
      </div>

      {/* Center: title + description */}
      <div style={styles.center}>
        <div style={styles.title}>{event.title}</div>
        <div style={styles.meta}>
          <span style={styles.actor}>{event.actor}</span>
          {event.has_attachments && <span style={styles.attachmentBadge}>📎</span>}
          {event.severity === 'high' || event.severity === 'critical' ? (
            <span style={styles.severityBadge}>{event.severity}</span>
          ) : null}
        </div>
      </div>

      {/* Right: pin button */}
      <div style={styles.right}>
        <button style={styles.pinBtn} onClick={togglePin} title="Pin event">
          {event.isPinned ? '📌' : '📍'}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  item: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  left: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 90 },
  badge: {
    padding: '2px 8px', borderRadius: 12, color: '#fff',
    fontSize: 11, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4,
  },
  time: { fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' },
  center: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 },
  actor: { fontSize: 12, color: '#6b7280' },
  attachmentBadge: { fontSize: 12 },
  severityBadge: {
    fontSize: 11, padding: '1px 6px', borderRadius: 8,
    background: '#fef2f2', color: '#dc2626', fontWeight: 600,
  },
  right: { display: 'flex', alignItems: 'center' },
  pinBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4 },
};
